/**
 * Perkecil gambar SEBELUM diunggah.
 *
 * Foto produk yang diunggah admin adalah muatan terberat di situs ini: satu
 * PNG bisa 1,4 MB, padahal di layar ia hanya ditampilkan selebar ~160 piksel.
 * Menyusutkannya di sisi peramban jauh lebih murah daripada memproses ulang di
 * server, dan mencegah berkas raksasa masuk ke S3 sejak awal.
 *
 * PNG diubah ke JPEG kecuali berkasnya memang butuh transparansi — logo produk
 * berlatar putih tidak, dan JPEG-nya bisa 10× lebih ringan.
 */

const LEBAR_MAKS = 1280
const MUTU = 0.82
/** Di bawah ini tidak usah diapa-apakan — ongkosnya lebih besar dari hasilnya. */
const AMBANG_BYTE = 200 * 1024

export interface HasilKompresi {
  file: File
  ukuranAsli: number
  ukuranBaru: number
}

function muatGambar(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error("Gambar tidak bisa dibaca"))
    }
    img.src = url
  })
}

/** Deteksi piksel tembus pandang — penentu boleh-tidaknya dijadikan JPEG. */
function adaTransparansi(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D): boolean {
  try {
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height)
    // Dicicipi tiap 40 piksel: cukup untuk mendeteksi latar tembus pandang,
    // tanpa menelusuri jutaan piksel satu per satu.
    for (let i = 3; i < data.length; i += 4 * 40) {
      if (data[i] < 250) return true
    }
    return false
  } catch {
    return true // canvas ternoda → jangan berasumsi, pertahankan format asal
  }
}

export async function kompresGambar(file: File): Promise<HasilKompresi> {
  const apaAdanya: HasilKompresi = { file, ukuranAsli: file.size, ukuranBaru: file.size }

  // SVG dan GIF (mungkin beranimasi) tidak boleh lewat canvas.
  if (!file.type.startsWith("image/") || /svg|gif/.test(file.type)) return apaAdanya
  if (file.size <= AMBANG_BYTE) return apaAdanya

  try {
    const img = await muatGambar(file)
    const skala = Math.min(1, LEBAR_MAKS / img.naturalWidth)
    const lebar = Math.round(img.naturalWidth * skala)
    const tinggi = Math.round(img.naturalHeight * skala)

    const canvas = document.createElement("canvas")
    canvas.width = lebar
    canvas.height = tinggi
    const ctx = canvas.getContext("2d", { willReadFrequently: true })
    if (!ctx) return apaAdanya
    ctx.drawImage(img, 0, 0, lebar, tinggi)

    const perluAlpha = file.type === "image/png" && adaTransparansi(canvas, ctx)
    const tipe = perluAlpha ? "image/png" : "image/jpeg"
    if (!perluAlpha) {
      // JPEG tidak punya alpha: beri latar putih supaya bagian tembus pandang
      // tidak berubah jadi hitam.
      ctx.globalCompositeOperation = "destination-over"
      ctx.fillStyle = "#ffffff"
      ctx.fillRect(0, 0, lebar, tinggi)
    }

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, tipe, MUTU),
    )
    if (!blob || blob.size >= file.size) return apaAdanya // sudah optimal

    const namaBaru = file.name.replace(/\.[^.]+$/, "") + (tipe === "image/jpeg" ? ".jpg" : ".png")
    return {
      file: new File([blob], namaBaru, { type: tipe, lastModified: Date.now() }),
      ukuranAsli: file.size,
      ukuranBaru: blob.size,
    }
  } catch {
    return apaAdanya // gagal mengecilkan bukan alasan menggagalkan unggahan
  }
}

export function formatUkuran(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  return `${Math.round(bytes / 1024)} KB`
}
